import useSEO from '@/hooks/useSEO';
import { spreadFAQs } from './data';
import BidAskSection from './components/BidAskSection';
import SpreadTypesSection from './components/SpreadTypesSection';
import SessionSpreadSection from './components/SessionSpreadSection';
import SpreadFactorsSection from './components/SpreadFactorsSection';
import SpreadImpactSection from './components/SpreadImpactSection';
import SpreadVsTotalSection from './components/SpreadVsTotalSection';
import SpreadFAQSection from './components/SpreadFAQSection';
import RelatedGuidesSection from './components/RelatedGuidesSection';

export default function ForexSpread() {
  useSEO({
    /* @replace:spread-title */

    title: 'Forex Spread Explained: Bid, Ask, Types and Cost',
    /* @replace:spread-description */

    description: 'Learn what forex spread is, how bid and ask prices create trading cost, why spreads widen, and how to calculate the spread cost of each trade.',
    /* @replace:spread-keywords */

    keywords: 'forex spread, bid ask spread, spread cost, fixed spread, variable spread, forex spread calculation',
    /* @replace:spread-canonical */

    canonical: '/forex-spread/',
  });

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: spreadFAQs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <div className="min-h-screen">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqStructuredData) }}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden bg-foreground-950">

        {/* Decorative: fine dot grid — price ladder / order book texture */}
        <div
          className="absolute inset-0 opacity-[0.025]"
          style={{
            backgroundImage: 'radial-gradient(circle, oklch(var(--accent-500)) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        {/* Decorative: two offset ring clusters — bid and ask as separate but paired forces */}
        <div className="absolute -top-[180px] -left-[100px] w-[520px] h-[520px] rounded-full border border-accent-500/8" />
        <div className="absolute -top-[130px] -left-[50px] w-[420px] h-[420px] rounded-full border border-accent-500/6" />
        <div className="absolute -top-[80px] left-[0px] w-[320px] h-[320px] rounded-full border border-accent-500/4" />

        <div className="absolute -bottom-[180px] -right-[100px] w-[520px] h-[520px] rounded-full border border-secondary-500/6" />
        <div className="absolute -bottom-[130px] -right-[50px] w-[420px] h-[420px] rounded-full border border-secondary-500/5" />
        <div className="absolute -bottom-[80px] right-[0px] w-[320px] h-[320px] rounded-full border border-secondary-500/3" />

        {/* Decorative: offset parallel lines — the spread gap metaphor */}
        <div className="absolute top-[90px] left-[12%] w-[140px] h-[1px] bg-gradient-to-r from-accent-500/0 via-accent-500/28 to-accent-500/0 rotate-[6deg]" />
        <div className="absolute top-[96px] left-[12%] w-[140px] h-[1px] bg-gradient-to-r from-secondary-500/0 via-secondary-500/22 to-secondary-500/0 rotate-[6deg]" />
        <div className="absolute top-[200px] right-[14%] w-[160px] h-[1px] bg-gradient-to-r from-accent-500/0 via-accent-500/30 to-accent-500/0 -rotate-[4deg]" />
        <div className="absolute top-[206px] right-[14%] w-[160px] h-[1px] bg-gradient-to-r from-secondary-500/0 via-secondary-500/20 to-secondary-500/0 -rotate-[4deg]" />

        {/* Decorative: thin vertical bars — price ladder markers */}
        <div className="absolute top-[25%] left-[5%] flex gap-2.5">
          <div className="w-[1px] h-14 bg-accent-500/8" />
          <div className="w-[1px] h-22 bg-accent-500/12" />
          <div className="w-[1px] h-10 bg-accent-500/6" />
          <div className="w-[1px] h-18 bg-accent-500/10" />
        </div>
        <div className="absolute bottom-[30%] right-[6%] flex gap-2.5">
          <div className="w-[1px] h-20 bg-secondary-500/10" />
          <div className="w-[1px] h-14 bg-secondary-500/8" />
          <div className="w-[1px] h-26 bg-secondary-500/6" />
          <div className="w-[1px] h-10 bg-secondary-500/10" />
        </div>

        {/* Decorative: scattered dot clusters — fractional pip hints */}
        <div className="absolute top-[35%] right-[10%] flex gap-1.5">
          <span className="w-1 h-1 rounded-full bg-accent-500/20" />
          <span className="w-1.5 h-1.5 rounded-full bg-accent-500/30" />
          <span className="w-1 h-1 rounded-full bg-accent-500/18" />
        </div>
        <div className="absolute bottom-[28%] left-[16%] flex gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-secondary-500/20" />
          <span className="w-1 h-1 rounded-full bg-secondary-500/22" />
          <span className="w-1 h-1 rounded-full bg-secondary-500/14" />
          <span className="w-1.5 h-1.5 rounded-full bg-secondary-500/16" />
        </div>

        {/* Decorative: scattered tiny crosses */}
        <span className="absolute top-[40%] right-[22%] text-accent-500/10 text-[16px] font-light select-none">+</span>
        <span className="absolute bottom-[35%] left-[25%] text-secondary-500/8 text-[14px] font-light select-none">+</span>
        <span className="absolute top-[50%] left-[30%] text-accent-500/7 text-[12px] font-light select-none">+</span>

        {/* Decorative: large soft glow behind center */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[750px] h-[500px] rounded-full opacity-[0.06] blur-[130px] bg-accent-500" />

        {/* Main content */}
        <div className="relative max-w-main mx-auto px-4 md:px-6 pt-16 md:pt-24 pb-16 md:pb-20 text-center">
          {/* Heading */}
          <h1 className="text-[38px] md:text-[56px] font-[750] text-background-50 leading-[1.08] tracking-tight mb-5">
            What is the
            <br />
            <span className="relative inline-block">
              <span className="relative z-10">forex spread</span>
              <span className="absolute left-0 right-0 bottom-[2px] h-[5px] rounded-full bg-accent-500/60" />
            </span>
          </h1>

          <p className="text-[16px] md:text-[18px] text-foreground-400 leading-relaxed max-w-[620px] mx-auto mb-10">
            The gap between buy and sell prices. The first cost you pay the second you enter a trade. The one that scalping traders feel the most.
          </p>

          {/* ── Core visual: The Spread Gap ── */}
          <div className="max-w-[860px] mx-auto">
            <div className="relative bg-foreground-900/80 border border-foreground-800/60 rounded-[24px] p-6 md:p-10 overflow-hidden">
              {/* Subtle inner glow */}
              <div className="absolute -top-[40px] right-[0px] w-[300px] h-[300px] bg-accent-500/3 rounded-full blur-[100px] pointer-events-none" />

              <div className="relative">
                {/* Formula label */}
                <div className="text-[12px] font-bold uppercase tracking-[0.15em] text-foreground-500 mb-6">
                  The formula
                </div>

                {/* Visual spread gap representation */}
                <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6 mb-8">
                  {/* BID box */}
                  <div className="w-full md:w-auto flex-1 max-w-[220px] bg-foreground-800/60 rounded-xl p-5 border border-foreground-700/40">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-accent-400" />
                      <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-foreground-500">Bid</span>
                    </div>
                    <div className="text-[28px] md:text-[34px] font-[800] text-background-50 leading-none tracking-tight tabular-nums">
                      1.2000
                    </div>
                    <div className="text-[11px] text-foreground-500 mt-1">You sell here</div>
                  </div>

                  {/* The gap indicator */}
                  <div className="flex flex-col items-center gap-1 flex-shrink-0">
                    <div className="hidden md:flex items-center gap-1">
                      <div className="w-8 h-[2px] bg-accent-500/40 rounded-full" />
                      <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[7px] border-l-accent-500/40" />
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-accent-400 whitespace-nowrap">
                      The gap
                    </span>
                    <span className="text-[13px] font-[700] text-accent-400 whitespace-nowrap">
                      2 pips
                    </span>
                    <div className="md:hidden flex items-center gap-1">
                      <div className="w-12 h-[2px] bg-accent-500/40 rounded-full" />
                      <div className="w-0 h-0 border-t-[5px] border-t-transparent border-b-[5px] border-b-transparent border-l-[7px] border-l-accent-500/40" />
                    </div>
                  </div>

                  {/* ASK box */}
                  <div className="w-full md:w-auto flex-1 max-w-[220px] bg-foreground-800/60 rounded-xl p-5 border border-foreground-700/40">
                    <div className="flex items-center justify-center gap-2 mb-2">
                      <div className="w-2 h-2 rounded-full bg-secondary-400" />
                      <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-foreground-500">Ask</span>
                    </div>
                    <div className="text-[28px] md:text-[34px] font-[800] text-background-50 leading-none tracking-tight tabular-nums">
                      1.2002
                    </div>
                    <div className="text-[11px] text-foreground-500 mt-1">You buy here</div>
                  </div>
                </div>

                {/* Formula line */}
                <div className="flex items-center justify-center gap-3 md:gap-5 flex-wrap">
                  <span className="text-[11px] font-bold uppercase tracking-[0.1em] text-foreground-400">Formula</span>
                  <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-center">
                    <span className="text-[20px] md:text-[28px] font-[750] text-background-50 tracking-tight">Spread</span>
                    <span className="text-[16px] md:text-[24px] font-[300] text-foreground-500">=</span>
                    <span className="text-[20px] md:text-[28px] font-[750] text-secondary-400 tracking-tight">Ask</span>
                    <span className="text-[16px] md:text-[24px] font-[300] text-foreground-500">−</span>
                    <span className="text-[20px] md:text-[28px] font-[750] text-accent-400 tracking-tight">Bid</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom insight line */}
          <p className="mt-8 text-[13px] text-foreground-500 max-w-[620px] mx-auto leading-relaxed">
            A <strong className="text-foreground-400">2-pip spread</strong> on EUR/USD means the market must move 2 pips in your favor just to break even. For scalpers executing 20+ trades a day, this is the <strong className="text-foreground-400">single largest cost driver</strong> — far outweighing commissions.
          </p>
        </div>
      </section>

      <BidAskSection />
      <SpreadTypesSection />
      <SessionSpreadSection />
      <SpreadFactorsSection />
      <SpreadImpactSection />
      <SpreadVsTotalSection />
      <SpreadFAQSection />
      <RelatedGuidesSection />
    </div>
  );
}